import React, { useState, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import HomeScreen from './components/HomeScreen';
import LetterGrid from './components/LetterGrid';
import PuzzleGame from './components/PuzzleGame';
import SuccessScreen from './components/SuccessScreen';
import SoundButton from './components/SoundButton';
import useSound from './hooks/useSound';
import { letters, LaoLetter } from './data/letters';
import { numbers, LaoNumber } from './data/numbers';

type Screen = 'home' | 'grid' | 'puzzle' | 'success';

function App() {
  const [screen, setScreen] = useState<Screen>('home');
  const [mode, setMode] = useState<'letters' | 'numbers'>('letters');
  const [currentItem, setCurrentItem] = useState<LaoLetter | LaoNumber | null>(null);
  const [muted, setMuted] = useState(false);
  const { speak, playEffect, toggleMute } = useSound();

  const handleSelectMode = useCallback((m: 'letters' | 'numbers') => {
    setMode(m);
    setScreen('grid');
  }, []);

  const handleSelectItem = useCallback((item: LaoLetter | LaoNumber) => {
    setCurrentItem(item);
    setScreen('puzzle');
  }, []);

  const handlePuzzleSuccess = useCallback(() => {
    playEffect('success');
    setScreen('success');
  }, [playEffect]);

  const handleNext = useCallback(() => {
    const items = mode === 'letters' ? letters : numbers;
    const currentIndex = items.findIndex(i => {
      if ('char' in i && currentItem && 'char' in currentItem) return i.char === (currentItem as LaoLetter).char;
      if ('laoChar' in i && currentItem && 'laoChar' in currentItem) return i.laoChar === (currentItem as LaoNumber).laoChar;
      return false;
    });
    const nextIndex = (currentIndex + 1) % items.length;
    const nextItem = items[nextIndex];
    setCurrentItem(nextItem);
    setScreen('puzzle');
    setTimeout(() => speak(nextItem.name), 300);
  }, [mode, currentItem, speak]);

  const handleReplay = useCallback(() => {
    setScreen('puzzle');
    if (currentItem) {
      setTimeout(() => speak(currentItem.name), 300);
    }
  }, [currentItem, speak]);

  const handleToggleMute = useCallback(() => {
    const nowMuted = toggleMute();
    setMuted(nowMuted);
  }, [toggleMute]);

  return (
    <div className="font-lao">
      <SoundButton isMuted={muted} onToggle={handleToggleMute} />

      <AnimatePresence mode="wait">
        {screen === 'home' && (
          <HomeScreen key="home" onSelectMode={handleSelectMode} />
        )}

        {screen === 'grid' && (
          <LetterGrid
            key="grid"
            mode={mode}
            onSelect={handleSelectItem}
            onBack={() => setScreen('home')}
            speak={speak}
          />
        )}

        {screen === 'puzzle' && currentItem && (
          <PuzzleGame
            key={`puzzle-${'char' in currentItem ? currentItem.char : currentItem.laoChar}`}
            item={currentItem}
            onSuccess={handlePuzzleSuccess}
            onBack={() => setScreen('grid')}
            speak={speak}
            playEffect={playEffect}
          />
        )}

        {screen === 'success' && currentItem && (
          <SuccessScreen
            key="success"
            item={currentItem}
            onNext={handleNext}
            onReplay={handleReplay}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
